import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { forgotPassword, confirmForgotPassword } from "../services/cognitoAuthService";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request"); // "request" | "confirm" | "done"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setStep("confirm");
    } catch (err) {
      setError(err?.message || "Couldn't send a reset code. Check the email and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmForgotPassword(email, code, newPassword);
      setStep("done");
    } catch (err) {
      setError(err?.message || "Couldn't reset your password. Check the code and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
        <Paper elevation={6} sx={{ p: 4, width: "100%", borderRadius: 2 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <LocalHospitalIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Reset your password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {step === "request" && "Enter your email and we'll send you a reset code."}
              {step === "confirm" && "Enter the code we sent, and choose a new password."}
              {step === "done" && "Your password has been reset."}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {step === "request" && (
            <Box component="form" onSubmit={handleRequestCode}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{ py: 1.5, textTransform: "none", background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)" }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : "Send reset code"}
              </Button>
            </Box>
          )}

          {step === "confirm" && (
            <Box component="form" onSubmit={handleConfirmReset}>
              <TextField
                fullWidth
                label="Reset code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                helperText="At least 8 characters, with uppercase, lowercase, and a number."
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{ py: 1.5, textTransform: "none", background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)" }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : "Reset password"}
              </Button>
            </Box>
          )}

          {step === "done" && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate("/login")}
              sx={{ py: 1.5, textTransform: "none", background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)" }}
            >
              Back to sign in
            </Button>
          )}

          {step !== "done" && (
            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Button variant="text" onClick={() => navigate("/login")} sx={{ textTransform: "none" }}>
                Back to sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
